import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Table, Button, Modal, Form, Card } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

const ManageVehicleSizes = () => {
    const { user } = useContext(AuthContext);
    const [sizes, setSizes] = useState([]);
    const [defaultSizeId, setDefaultSizeId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentSize, setCurrentSize] = useState({});

    const canManageSizes = hasPermission(user?.role, 'settings:sizes');

    const fetchVehicleSizes = useCallback(async () => {
        if (!user) return;
        try {
            const response = await api.get('/vehiclesizes');
            setSizes(response.data.data.sizes);
            setDefaultSizeId(response.data.data.defaultSizeId);
        } catch (error) {
            console.error("Error fetching vehicle sizes:", error);
        }
    }, [user]);

    useEffect(() => {
        if (user) fetchVehicleSizes();
    }, [user, fetchVehicleSizes]);

    const handleShowModal = (size = null) => {
        setIsEditing(size !== null);
        setCurrentSize(size ? { ...size } : { sizeName: '' });
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleSave = async () => {
        if (!user) return;
        try {
            if (isEditing) {
                await api.put(`/vehiclesizes/${currentSize.id}`, currentSize);
            } else {
                await api.post('/vehiclesizes', { ...currentSize, displayOrder: sizes.length + 1 });
            }
            fetchVehicleSizes();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving vehicle size:", error);
        }
    };

    const handleDelete = async (id) => {
        if (!user) return;
        if (window.confirm('정말로 이 사이즈를 삭제하시겠습니까? 이 사이즈에 연결된 모든 가격 정보가 함께 삭제됩니다.')) {
            try {
                await api.delete(`/vehiclesizes/${id}`);
                fetchVehicleSizes();
            } catch (error) {
                console.error("Error deleting vehicle size:", error);
            }
        }
    };

    const handleSetDefault = async (sizeId) => {
        if (!user || !canManageSizes) return;
        try {
            await api.put('/vehiclesizes/default', { sizeId });
            setDefaultSizeId(sizeId);
        } catch (error) {
            console.error("Error setting default vehicle size:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentSize(prev => ({ ...prev, [name]: value }));
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        const items = Array.from(sizes);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);
        setSizes(items);
        try {
            await api.put('/vehiclesizes/reorder', { order: items.map(s => ({ id: s.id })) });
        } catch (error) {
            console.error("Error saving order:", error);
            fetchVehicleSizes();
        }
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>차량 사이즈</h3>
                    <p>차량 사이즈 분류와 기본값을 관리합니다</p>
                </div>
                <div className="page-header-actions">
                    {canManageSizes && (
                        <Button variant="primary" size="sm" onClick={() => handleShowModal()}>새 사이즈 추가</Button>
                    )}
                </div>
            </div>
            <Card>
                <Card.Body>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    {canManageSizes && <th style={{ width: 40 }}></th>}
                                    <th>기본값</th>
                                    <th>사이즈명</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <Droppable droppableId="sizes">
                                {(provided) => (
                                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                        {sizes.map((size, index) => (
                                            <Draggable key={String(size.id)} draggableId={String(size.id)} index={index} isDragDisabled={!canManageSizes}>
                                                {(provided, snapshot) => (
                                                    <tr ref={provided.innerRef} {...provided.draggableProps}
                                                        style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? '#f0f0f0' : undefined }}>
                                                        {canManageSizes && <td {...provided.dragHandleProps} style={{ cursor: 'grab', textAlign: 'center' }}>☰</td>}
                                                        <td>
                                                            <Form.Check
                                                                type="radio"
                                                                name="defaultSize"
                                                                id={`default-size-${size.id}`}
                                                                checked={defaultSizeId === size.id}
                                                                onChange={() => handleSetDefault(size.id)}
                                                                disabled={!canManageSizes}
                                                            />
                                                        </td>
                                                        <td>{size.sizeName}</td>
                                                        <td>
                                                            {canManageSizes && (
                                                                <>
                                                                    <Button variant="info" size="sm" onClick={() => handleShowModal(size)}>수정</Button>{' '}
                                                                    <Button variant="danger" size="sm" onClick={() => handleDelete(size.id)}>삭제</Button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? '사이즈 수정' : '새 사이즈 추가'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>사이즈명</Form.Label>
                            <Form.Control type="text" name="sizeName" value={currentSize.sizeName || ''} onChange={handleChange} disabled={!canManageSizes} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>닫기</Button>
                    <Button variant="primary" onClick={handleSave} disabled={!canManageSizes}>저장</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageVehicleSizes;
